import type { Math } from "@pdfking/quantity"
import { Ast, CM, MM } from "@pdfking/quantity"
import { RecordMerge } from "@pdfking/tfunc"

import type { GrowContentMeta, GrowEdge, GrowKindFlags, GrowLayout } from "#public"
import {
  Across,
  Along,
  Content,
  DIM_CONST,
  DIM_DEPTH,
  DIM_HEIGHT,
  DIM_NUM,
  DIM_SHEET_DEPTH,
  DIM_TIMES,
  DIM_WIDTH,
  DIM_ZERO,
  Face,
  Flap,
  FlapPiece,
  Fold,
  Outward,
  Pages,
  RecordByPrimaryKey,
  makeGrowEntry,
  ContentRenderer,
  GrowContentKind,
  NO_PAGES,
} from "#public"

import { DieCutStudioGrowList } from "./DieCutStudioGrowList"

// A sheet's thickness, as the dies take it: never under half a millimetre
const sheet: Math = Ast.Max(DIM_SHEET_DEPTH, DIM_CONST(MM(0.5)))

const pkg_flap = Ast.Min(
  Ast.Max(DIM_CONST(CM(10)), DIM_DEPTH),
  Ast.Mul(DIM_WIDTH, DIM_NUM(0.25)),
  Ast.Mul(DIM_HEIGHT, DIM_NUM(0.25))
)

// A panel of the die: a face of the box, `A` the page's, or a strip no face.
const panel = (
  size: Math,
  meta: GrowContentMeta = NO_PAGES,
  cross: Math | undefined = undefined
): GrowLayout => Content(GrowContentKind.PAGE, size, meta, cross)
const FRONT = Pages(1, 2, { label: "A" })

// A wall standing off `edge` of a tray's base reads with its foot on the
// hinge
const STANDING: Record<GrowEdge, GrowContentMeta["rotation"]> = {
  top: 0,
  right: 270,
  bottom: 180,
  left: 90,
}

// Faces are lettered as a box seen from its front: A the front — the page's
// face —, B its right, C the back, D its left, E the top, F the bottom; a
// tray A its base, B to E its walls round from the front, F its lid. Seen
// from the front a top reads away from it, a bottom towards it.

// The tuck box: the four walls round the page's width, the glue tab past
// the first, in a tube that unfolds last; a tuck flap off the top and the
// bottom of the page's wall, pulled out first, its tongue folded in before
// the flap closes so it slides inside the box, a touch past square to sit
// within the wall; a dust flap off each end of the walls beside, cut in at
// its sides.
const tuck = (edge: GrowEdge, face: GrowContentMeta): GrowLayout =>
  Flap(
    edge,
    Outward(edge, [panel(DIM_DEPTH, face), Fold(90, 2, -0.25), FlapPiece(DIM_CONST(CM(1.5)))]),
    {
      angle: 90,
      step: 1,
    }
  )
const dust = (edge: GrowEdge): GrowLayout =>
  Flap(edge, FlapPiece(DIM_DEPTH), {
    angle: 90,
    step: 2,
    taper: [Ast.Mul(DIM_DEPTH, DIM_NUM(0.3)), Ast.Mul(DIM_DEPTH, DIM_NUM(0.3))],
  })
const TUBE = Fold(90, 3)
const tuck_box = (): GrowLayout =>
  Along(
    [
      panel(DIM_HEIGHT, Face("C")),
      Flap("left", FlapPiece(DIM_CONST(CM(2))), { angle: 90, step: 3 }),
      TUBE,
      panel(DIM_DEPTH, Face("D")),
      dust("top"),
      dust("bottom"),
      TUBE,
      panel(DIM_HEIGHT, FRONT),
      tuck("top", Face("E")),
      tuck("bottom", Face("F")),
      TUBE,
      panel(DIM_DEPTH, Face("B")),
      dust("top"),
      dust("bottom"),
    ],
    DIM_WIDTH
  )

// A packaging's wall off `edge` of its base, face `label`, `over` folded
// over it: a sheet or two of play at each crease. The flaps close first.
const play = Ast.Mul(DIM_NUM(2), Ast.Max(DIM_SHEET_DEPTH, DIM_CONST(MM(1))))
const wall = (edge: GrowEdge, label: string, over: GrowLayout): GrowLayout =>
  Flap(
    edge,
    Outward(edge, [
      panel(Ast.Add(DIM_DEPTH, play), Face(label, STANDING[edge])),
      Fold(90, 1),
      over,
    ]),
    { angle: 90, step: 2 }
  )
const pkg_over = FlapPiece(Ast.Add(pkg_flap, play))

// The packaging: the base the page, a wall on each side, a flap over each;
// its front the top wall.
const packaging = (): GrowLayout =>
  Along([
    panel(DIM_WIDTH, FRONT, DIM_HEIGHT),
    wall("left", "E", pkg_over),
    wall("right", "C", pkg_over),
    wall("top", "B", pkg_over),
    wall("bottom", "D", pkg_over),
  ])

// The packaging with its lid: the page's width over the right wall, its
// back, a flap over the left, its front; the lid reads towards its hinge.
const packaging_lid = (): GrowLayout =>
  Along([
    panel(DIM_WIDTH, FRONT, DIM_HEIGHT),
    wall("left", "B", pkg_over),
    wall("right", "D", panel(Ast.Add(DIM_WIDTH, play), Face("F", 90))),
    wall("top", "C", pkg_over),
    wall("bottom", "E", pkg_over),
  ])

// The auto box, a tray and its lid in one piece. Across the opening: an
// inner wall, its lock tab off its edge, a strip two sheets wide, the side
// wall, the base, and the same the other way. The front wall off the base's
// top, the back wall off its bottom, each with a corner flap at either end,
// cut in by the base; the lid off the back wall, a sheet short of the base,
// and its front flap off the lid: tapered, or with an ear at either end
// when `ears`. Closing, the corner flaps turn in first, the front and back
// walls go up, the side walls over them and the inner walls down inside,
// locked; then the lid's front flap folds and the lid comes down.
const auto_box = (ears: boolean): GrowLayout => {
  const span = Ast.Add(DIM_WIDTH, DIM_TIMES(sheet, 3))
  const corner_depth = Ast.Add(
    Ast.Min(DIM_TIMES(DIM_DEPTH, 1.5), Ast.Sub(DIM_TIMES(DIM_WIDTH, 0.5), DIM_CONST(MM(2)))),
    sheet
  )
  const notch = Ast.Add(DIM_CONST(MM(5)), DIM_TIMES(sheet, 0.5))
  const corner = (edge: GrowEdge, from: Math, taper: [Math, Math]): GrowLayout =>
    Flap(edge, FlapPiece(corner_depth), {
      angle: 90,
      step: 6,
      from,
      length: Ast.Sub(DIM_DEPTH, sheet),
      taper,
    })
  const ear_width = Ast.Min(DIM_DEPTH, DIM_CONST(CM(3)))
  const ear = (edge: GrowEdge): GrowLayout =>
    Flap(edge, FlapPiece(ear_width), {
      angle: 90,
      step: 3,
      from: DIM_CONST(MM(2.5)),
      length: Ast.Sub(DIM_DEPTH, DIM_CONST(MM(2.5))),
      taper: [DIM_TIMES(ear_width, 0.1), DIM_TIMES(ear_width, 0.8)],
    })
  const front_flap = ears
    ? Flap("bottom", Across([FlapPiece(DIM_DEPTH), ear("left"), ear("right")], span), {
        angle: 90,
        step: 2,
      })
    : Flap("bottom", FlapPiece(DIM_DEPTH), {
        angle: 90,
        step: 2,
        taper: [DIM_CONST(MM(5)), DIM_CONST(MM(5))],
      })
  const front = Flap(
    "top",
    Across(
      [
        panel(DIM_DEPTH, Face("B")),
        corner("left", sheet, [DIM_ZERO, notch]),
        corner("right", sheet, [DIM_ZERO, notch]),
      ],
      span
    ),
    { angle: 90, step: 5, from: sheet, length: span }
  )
  const back = Flap(
    "bottom",
    Across(
      [
        panel(Ast.Add(DIM_DEPTH, sheet), Face("D", 180)),
        corner("left", DIM_ZERO, [notch, DIM_ZERO]),
        corner("right", DIM_ZERO, [notch, DIM_ZERO]),
        Fold(90, 1),
        panel(Ast.Add(DIM_HEIGHT, sheet), Face("F")),
        front_flap,
      ],
      span
    ),
    { angle: 90, step: 5, from: sheet, length: span }
  )
  const inner = FlapPiece(Ast.Sub(DIM_DEPTH, DIM_CONST(MM(2))))
  const tab = (edge: GrowEdge): GrowLayout =>
    Flap(edge, FlapPiece(Ast.Add(DIM_CONST(MM(3)), sheet)), {
      from: Ast.Add(DIM_TIMES(DIM_HEIGHT, 1 / 3), sheet),
      length: DIM_TIMES(DIM_HEIGHT, 1 / 3),
    })
  const strip = FlapPiece(DIM_TIMES(sheet, 2))
  const WALL = Fold(90, 4)
  const OVER = Fold(90, 3)
  return Along(
    [
      inner,
      tab("left"),
      OVER,
      strip,
      OVER,
      panel(DIM_DEPTH, Face("E", 90)),
      WALL,
      panel(Ast.Add(DIM_WIDTH, DIM_TIMES(sheet, 5)), FRONT),
      front,
      back,
      WALL,
      panel(DIM_DEPTH, Face("C", 270)),
      OVER,
      strip,
      OVER,
      inner,
      tab("right"),
    ],
    Ast.Add(DIM_HEIGHT, DIM_TIMES(sheet, 2))
  )
}

// The box cover, a sleeve: the page's width, the depth, the width again and
// the depth round the box, a glue tab past them that folds in first.
const TUBE_COVER = Fold(90, 1)
const box_cover = (): GrowLayout =>
  Along(
    [
      panel(DIM_WIDTH, FRONT),
      TUBE_COVER,
      panel(DIM_DEPTH, Face("B")),
      TUBE_COVER,
      panel(DIM_WIDTH, Face("C")),
      TUBE_COVER,
      panel(DIM_DEPTH, Face("D")),
      Fold(90, 2),
      FlapPiece(DIM_CONST(CM(2))),
    ],
    DIM_HEIGHT
  )

// A box: one page over the whole die, its die-lines drawn by its plotter, its
// security kept inside every face.
const box = <I extends string>(
  id: I,
  name: string,
  layout: GrowLayout,
  flags: GrowKindFlags = {}
) =>
  makeGrowEntry(id, name, "BOX", layout, {
    is_box: true,
    is_single_page: true,
    renderer: ContentRenderer.BOX,
    show_cut: false,
    show_crossmark: false,
    ...flags,
  })

// The boxes drawn here, and Die Cut Studio's; their envelopes are a list of
// their own.
export const BoxGrowList = RecordMerge(
  RecordByPrimaryKey([
    box("bx1", "BOX", tuck_box()),
    box("bxa", "GROW_KIND_BXA", auto_box(false)),
    box("bxaf", "GROW_KIND_BXAF", auto_box(true)),
    box("pack", "PACKAGING", packaging()),
    box("packa", "GROW_KIND_PACKA", packaging_lid()),
    box("bcv", "GROW_KIND_BCV", box_cover(), {
      show_cut: true,
      show_crossmark: true,
    }),
  ] as const),
  DieCutStudioGrowList
)
